import { getLocale } from "@/paraglide/runtime.js";
import { periodDays, utcMidnight } from "@/shared/billing-cycle.ts";
import { microToEurAmount } from "@/shared/money.ts";

export { formatBytes } from "@/shared/file-size.ts";

/**
 * The regional tag every formatter in this file uses.
 *
 * `getLocale()` returns the Paraglide tag ("de"/"en"); Intl wants a regional one.
 * This ternary was written out nine times across the repo, so a formatter added
 * later could quietly disagree with the rest about what "de" means.
 */
const appLocale = (): string => (getLocale() === "de" ? "de-DE" : "en-GB");

/**
 * Currency rendering goes through `Intl.NumberFormat`, not string templates.
 *
 * A hand-built `€${n.toFixed(2)}` gets two things wrong at once: it never groups
 * thousands (an invoice roll-up reads "€1234.56"), and it hardcodes the symbol in
 * front, which is simply not how German writes money — "1.234,56 €", symbol
 * trailing, comma decimal, dot grouping. This file already resolves the app
 * locale for dates; money now uses the same source rather than assuming English.
 *
 * Formatters are cached because these run per table cell — a report table with a
 * few hundred rows would otherwise construct a few hundred identical ones.
 */
const currencyFormatters = new Map<string, Intl.NumberFormat>();

const currencyFormatter = (fractionDigits: number): Intl.NumberFormat => {
	const locale = appLocale();
	const key = `${locale}:${fractionDigits}`;
	let formatter = currencyFormatters.get(key);
	if (formatter === undefined) {
		formatter = new Intl.NumberFormat(locale, {
			style: "currency",
			currency: "EUR",
			minimumFractionDigits: fractionDigits,
			maximumFractionDigits: fractionDigits,
		});
		currencyFormatters.set(key, formatter);
	}
	return formatter;
};

/** Format cents (e.g. 2299) as "€22.99" / "22,99 €". */
export function fmtCents(cents: number): string {
	return currencyFormatter(2).format(cents / 100);
}

/** Format microcents (e.g. 500_000_000 = €5.00). 1 cent = 1,000,000 micro. 1 EUR = 100,000,000 micro. Returns zero for null. */
export function microToEur(micro: number | null): string {
	return currencyFormatter(2).format(microToEurAmount(micro ?? 0));
}

/**
 * Format a euro amount at full precision for usage/consumption figures — never
 * rounded to whole cents. 2 decimals at or above €1, 4 decimals below (e.g.
 * €0.0957). Charges use {@link fmtCents} instead, since Stripe bills whole cents.
 */
export function fmtEurPrecise(euros: number): string {
	if (euros === 0) {
		return currencyFormatter(2).format(0);
	}
	return currencyFormatter(Math.abs(euros) >= 1 ? 2 : 4).format(euros);
}

/** Like {@link fmtEurPrecise} but takes microcents. Returns zero for null. */
export function microToEurPrecise(micro: number | null): string {
	if (micro === null) {
		return currencyFormatter(2).format(0);
	}
	return fmtEurPrecise(microToEurAmount(micro));
}

/**
 * Format a euro amount as a whole-cent currency string. Tolerates null/undefined
 * (→ zero) because it formats nested, sometimes-optional report fields
 * (totals/segments/usage); a missing field must not crash the render.
 */
export function fmtEur(euros: number | null | undefined): string {
	return currencyFormatter(2).format(euros ?? 0);
}

/**
 * Non-currency formatters, cached for the same reason the currency ones are:
 * these run per table cell, and `fmtNumber` in particular is called from chart
 * labels and token counts where a few hundred identical constructions per render
 * is the normal case rather than the exception.
 */
const plainFormatters = new Map<string, Intl.NumberFormat>();

/**
 * Cache for the two shaped number formatters below.
 *
 * They live here rather than in the components that need them because three
 * billing components had each inlined `getLocale() === "de" ? "de-DE" : "en-GB"`
 * beside their own `Intl.NumberFormat` — the exact duplication this file's
 * `appLocale` exists to prevent, reintroduced one component at a time.
 *
 * KEYED ON THE LOCALE, like both caches above. Keying on the style alone pinned
 * whichever locale resolved first: Paraglide settles the locale at boot with
 * `setLocale(..., { reload: false })`, so a formatter built before that lands
 * would keep English grouping beside German currency on the same card.
 */
const specialFormatters = new Map<string, Intl.NumberFormat>();

const PLAIN_OPTIONS: Record<
	"decimal" | "percent" | "quantity",
	Intl.NumberFormatOptions
> = {
	decimal: {},
	percent: { style: "percent", maximumFractionDigits: 2 },
	quantity: { maximumFractionDigits: 6 },
};

const plainFormatter = (
	style: "decimal" | "percent" | "quantity",
): Intl.NumberFormat => {
	const locale = appLocale();
	const key = `${locale}:${style}`;
	let formatter = plainFormatters.get(key);
	if (formatter === undefined) {
		formatter = new Intl.NumberFormat(locale, PLAIN_OPTIONS[style]);
		plainFormatters.set(key, formatter);
	}
	return formatter;
};

/**
 * Format a plain count with locale grouping (e.g. 12345 → "12,345" / "12.345").
 *
 * `n.toLocaleString()` with no argument uses the BROWSER's locale, so a German
 * user on an English browser saw English grouping next to German currency on the
 * same card. This routes counts through the app locale like everything else.
 */
export function fmtNumber(n: number): string {
	return plainFormatter("decimal").format(n);
}

/**
 * A billed quantity at the precision it was STORED and priced at — up to six places,
 * trailing zeros dropped (6.333333 → "6.333333", 5 → "5").
 *
 * Not `fmtNumber`, which stops at three: a charge line's amount is `round(unit ×
 * quantity)` on the six-place quantity, so "0.145 × €129.00" beside €18.73 invites a
 * reader to multiply and land on €18.71.
 */
export function fmtQuantity(n: number): string {
	return plainFormatter("quantity").format(n);
}

/**
 * Compact notation for a large quantity — 1_495_072_748 → "1.5B" / "1,5 Mrd.".
 *
 * For counts nobody checks to the unit. Token totals on a billing report run to
 * ten figures, which is unreadable as digits and reads as noise rather than as
 * the QUANTITY behind a charge.
 */
export function fmtTokens(n: number): string {
	const key = `${appLocale()}:compact`;
	let formatter = specialFormatters.get(key);
	if (formatter === undefined) {
		formatter = new Intl.NumberFormat(appLocale(), {
			notation: "compact",
			maximumFractionDigits: 1,
		});
		specialFormatters.set(key, formatter);
	}
	return formatter.format(n);
}

/**
 * A day count at three decimals — "1.494", the precision a prorated line needs.
 *
 * The stored `durationDays` on a billing segment is rounded to two places, and at
 * a slice length of a day or two that rounding is worth cents: multiplying out
 * "1.49 days" lands four cents below the charge it sits under. Three places is
 * enough that the arithmetic on screen reproduces the amount beside it.
 */
export function fmtDays(days: number): string {
	const key = `${appLocale()}:days`;
	let formatter = specialFormatters.get(key);
	if (formatter === undefined) {
		formatter = new Intl.NumberFormat(appLocale(), {
			maximumFractionDigits: 3,
		});
		specialFormatters.set(key, formatter);
	}
	return formatter.format(days);
}

/**
 * Format a basis-point rate as a percentage (e.g. 1500 → "15%", 1550 → "15.5%").
 *
 * Commission rates and tier discounts are stored in basis points; every call site
 * was dividing by 100 inline and interpolating a bare "%", which skipped the
 * locale's decimal separator. Trailing zeros are dropped so a whole rate reads
 * "15%" rather than "15.0%".
 */
export function fmtPercent(basisPoints: number): string {
	return plainFormatter("percent").format(basisPoints / 10_000);
}

/** Format an ISO date string as a locale-aware short date (e.g. "15 Apr 2026"). Returns "—" for null. */
export function fmtDate(iso: string | null): string {
	if (!iso) {
		return "—";
	}
	const locale = appLocale();
	return new Date(iso).toLocaleDateString(locale, {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

/**
 * Format a 'YYYY-MM' accrual period as a locale-aware month (e.g. "Jul 2026",
 * "Juli 2026" in German).
 *
 * A period is a calendar month, not an instant — it is built with `Date.UTC` and
 * formatted in UTC so it cannot slip to the previous month for a viewer west of
 * UTC, the same trap the spend-trend axis had. Returns the raw value unchanged
 * if it is not a well-formed period, so a bad row renders as itself rather than
 * as "Invalid Date".
 */
export function fmtMonth(
	period: string,
	style: "short" | "long" = "short",
): string {
	const match = /^(\d{4})-(\d{2})$/.exec(period);
	if (!match) {
		return period;
	}
	const locale = appLocale();
	const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
	return date.toLocaleDateString(locale, {
		month: style,
		year: "numeric",
		timeZone: "UTC",
	});
}

/** Format an ISO timestamp as a locale-aware short date + time (e.g. "15 Apr 2026, 14:02"). Returns "—" for null. */
export function fmtDateTime(iso: string | null | undefined): string {
	if (!iso) {
		return "—";
	}
	const locale = appLocale();
	return new Date(iso).toLocaleString(locale, {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

/** Format an ISO timestamp as a locale-aware time of day (e.g. "14:32"). Returns "—" for null. */
export function fmtTime(iso: string | null | undefined): string {
	if (!iso) {
		return "—";
	}
	return new Date(iso).toLocaleTimeString(appLocale(), {
		hour: "2-digit",
		minute: "2-digit",
	});
}

/**
 * Format an ISO date in UTC (e.g. "1 Aug 2026"). Returns "—" for null.
 *
 * Sibling of {@link fmtDate}, which renders in the viewer's zone — right for
 * "you edited this yesterday", wrong for a billing surface. A change stamped
 * 2026-08-01T00:30:00Z renders as "31 Jul" for every viewer west of UTC, which
 * on a report page dates a subscription change BEFORE the period containing it.
 */
export function fmtDateUtc(iso: string | null | undefined): string {
	if (!iso) {
		return "—";
	}
	return new Date(iso).toLocaleDateString(appLocale(), {
		month: "short",
		day: "numeric",
		year: "numeric",
		timeZone: "UTC",
	});
}

/**
 * Format an ISO timestamp in UTC (e.g. "1 Oct 2026, 03:00").
 *
 * Sibling of {@link fmtDateTime}, which renders in the viewer's zone — right for
 * "you edited this 10 minutes ago", wrong for a billing surface where every
 * other date on the screen is a UTC period boundary. Mixing the two clocks on
 * one page is how a run scheduled for the 1st reads as the 30th.
 */
export function fmtDateTimeUtc(iso: string | null | undefined): string {
	if (!iso) {
		return "—";
	}
	return new Date(iso).toLocaleString(appLocale(), {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		timeZone: "UTC",
	});
}

/**
 * Format a billing-period range that must survive a year boundary, in UTC.
 *
 * A SIBLING of {@link fmtDateRange} rather than a fix to it. That one renders
 * "5 May – 5 Jun" in the viewer's local zone and has ~28 partner call sites plus
 * main-product usage, all of which want exactly that; widening it would change
 * every one of them.
 *
 * The billing surfaces need the opposite two properties, for reasons that are
 * not cosmetic:
 *
 *   THE YEAR IS ALWAYS PRINTED, on both ends, even when they share it. Periods
 *     are 30-day anniversary windows, so a range routinely crosses New Year
 *     ("18 Dec 2026 – 17 Jan 2027"). Someone auditing a bill should never have to
 *     infer which year a date belongs to from the invoice it arrived on.
 *   THE RENDER IS PINNED TO UTC. A period stored as ending 2027-01-01T00:00:00Z
 *     reads as 31 Dec 2026 for every viewer west of UTC — which places the period
 *     in a different month from the invoice that claimed it, and makes the same
 *     bill disagree with itself depending on who opened it.
 */
export function fmtDateRangeUtc(startIso: string, endIso: string): string {
	const locale = appLocale();
	const opts = {
		month: "short",
		day: "numeric",
		year: "numeric",
		timeZone: "UTC",
	} as const;
	const start = new Date(startIso).toLocaleDateString(locale, opts);
	const end = new Date(endIso).toLocaleDateString(locale, opts);
	return `${start} – ${end}`;
}

/**
 * A bill's SERVICE DAYS: its first day to its last day, inclusive — the days the ERP
 * row prints and the customer's dashboard labels the period with ("1 Sep 2026 –
 * 30 Sep 2026"). The stored period end is an EXCLUSIVE boundary: printed as is, it
 * names a day the bill does not cover, and for a period ending on the 1st, a day in the
 * next month. Rendered exactly as {@link fmtDateRangeUtc} (UTC, the year on both ends);
 * only the days differ.
 */
export function fmtServiceDays(
	periodStartIso: string,
	periodEndIso: string,
): string {
	const { firstDay, lastDay } = periodDays(
		new Date(periodStartIso),
		new Date(periodEndIso),
	);
	return fmtDateRangeUtc(
		utcMidnight(firstDay).toISOString(),
		utcMidnight(lastDay).toISOString(),
	);
}

/**
 * A range of CALENDAR DAYS, compact ("6 Aug – 5 Sep"). Takes `YYYY-MM-DD` days and reads
 * them on the UTC calendar, so no viewer's time zone can move a day — the days a bill
 * counts and prints are UTC days.
 */
export function fmtDayRange(firstDay: string, lastDay: string): string {
	const locale = appLocale();
	const opts = { month: "short", day: "numeric", timeZone: "UTC" } as const;
	const fmt = (day: string): string =>
		utcMidnight(day).toLocaleDateString(locale, opts);
	return `${fmt(firstDay)} – ${fmt(lastDay)}`;
}

/** Format a billing-period range as a compact day+month range (e.g. "5 May – 5 Jun"). */
export function fmtDateRange(startIso: string, endIso: string): string {
	const locale = appLocale();
	const opts = { month: "short", day: "numeric" } as const;
	const start = new Date(startIso).toLocaleDateString(locale, opts);
	const end = new Date(endIso).toLocaleDateString(locale, opts);
	return `${start} – ${end}`;
}
